import axios from 'axios';

/**
 * Sends a POST request to create a new reservation.
 * @param {string} name - Name of the person making the reservation.
 * @param {number} adults - Number of adults.
 * @param {number} children - Number of children.
 * @param {boolean} babychair - Whether a baby chair is required.
 * @param {string} table - Document ID of the table.
 * @param {string} time - Time of the reservation in ISO 8601 format.
 * @param {Array<Object>} menuassignment - Menu assignment details.
 * @param {number} menuassignment[].adults - Number of adults for this menu.
 * @param {number} menuassignment[].children - Number of children for this menu.
 * @param {string} menuassignment[].menu - Document ID of the menu.
 * @returns {Promise<Object>} - The response from the server.
 */

export async function createReservation(name, adults, children, babychair, table, time, contact, additionalInfo, source, menuassignment) {
   const apiUrl = `https://baeren-table-api.cr.baeren-lechtal.com/create-reservation`;

   const reservationData = {
       name,
       adults,
       children,
       babychair,
       table,
       time,
       contact,
       additionalInfo,
       source,       
       menuassignment,
   };

   try {
       const response = await axios.post(apiUrl, reservationData, {
           headers: {
               'Content-Type': 'application/json',
           },
       });

       // Handle the response
       return response.data;
   } catch (error) {
       // Handle errors
       console.error('Error creating reservation:', error.response?.data || error.message);
       throw error;
   }
}

/**
 * Fetches the best table and menu information for a given date and number of persons.
 * 
 * @param {object} date - A dayjs object representing the date.
 * @param {number} persons - Number of persons.
 * @returns {Promise<object>} - A promise resolving to the best table and menu data, or an error message.
 */
export const fetchBestTableAndMenu = async (date, persons) => {
  try {
    const formattedDate = date.format('YYYY-MM-DD'); // Format the date for the API

    // Call the "best table" API
    const tableResponse = await axios.get(
      `https://baeren-table-api.cr.baeren-lechtal.com/best-table/${formattedDate}/${persons}`
    );

    if (tableResponse.status === 200) {
      const bestTable = tableResponse.data.bestTable;

      // Call the "menu" API if a table is found
      const menuResponse = await axios.get(
        `https://baeren-table-api.cr.baeren-lechtal.com/menu/${formattedDate}`
      );

      if (menuResponse.status === 200) {
        const menu = menuResponse.data.menus;

        return {
          bestTable,
          menu,
        };
      } else {
        console.warn('No menu found for the given date');
        return { bestTable, menu: [] };
      }
    } else {
      console.warn('No suitable table found for the given criteria');
      return { error: 'No suitable table found' };
    }
  } catch (error) {
    console.error('Error fetching best table or menu:', error);
    return { error: error.message };
  }
};

/**
 * Prüft, ob ein bestimmtes Datum geöffnet ist.
 * @param {string} date - Das Datum im Format 'YYYY-MM-DD'.
 * @returns {Promise<boolean>} - `true` wenn offen, `false` wenn geschlossen.
 */
export const isDateOpened = async (date) => {
  try {
    const formattedDate = date.format('YYYY-MM-DD'); // Format the date for the API
    const response = await axios.get(`https://baeren-table-api.cr.baeren-lechtal.com/is-opened/${formattedDate}`);

    if (response.status === 200) {
      return response.data.is_opened;
    } else {
      console.error('Fehler beim Abrufen des Status:', response.status);
      return false; // Standardmäßig geschlossen, wenn ein Fehler auftritt
    }
  } catch (error) {
    console.error('Fehler beim Überprüfen des Datums:', error.message);
    return false; // Standardmäßig geschlossen, wenn ein Fehler auftritt
  }
};

/**
 * Holt die Verfügbarkeit der Tische von der API.
 *
 * @param {string} startDate - Startdatum im Format YYYY-MM-DD.
 * @param {string} endDate - Enddatum im Format YYYY-MM-DD.
 * @returns {Promise<Object>} - Promise, das die Tischverfügbarkeit zurückgibt.
 *                              Format: [{ date: "YYYY-MM-DD", freeTables: number }]
 */
export const fetchAvailableTables = async (startDate, endDate) => {
  try {
    const response = await axios.get(`https://baeren-table-api.cr.baeren-lechtal.com/reserved-tables/${startDate}/${endDate}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching table availability:", error);
    throw error;
  }
};